import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminApi, getAdmin } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ShieldAlert, UserPlus } from "lucide-react";

export default function Settings() {
  const admin = getAdmin();
  const isOwner = admin?.role === "owner";
  const [settings, setSettings] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ email: "", password: "", role: "reviewer" });

  const load = () => {
    adminApi.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    if (isOwner) adminApi.get("/admin/admins").then((r) => setAdmins(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const toggleTestMode = async (v) => {
    try {
      await adminApi.patch("/admin/settings", { test_mode: v });
      toast.success(`Test mode ${v ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Only the owner can change settings");
    }
  };

  const purge = async () => {
    try {
      const r = await adminApi.post("/admin/test-data/purge");
      toast.success(`Removed ${r.data.removed} TEST declarations`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    }
  };

  const createAdmin = async () => {
    try {
      await adminApi.post("/admin/admins", newAdmin);
      toast.success("Admin created");
      setNewAdmin({ email: "", password: "", role: "reviewer" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  if (!settings) return null;

  return (
    <div className="max-w-4xl space-y-6">
      <h1 className="font-heading font-bold text-2xl text-foreground" data-testid="admin-settings-title">Settings</h1>

      <Card><CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading font-semibold text-base">Test mode</h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-md">
              While enabled, every public declaration is forced to is_test=true and the site shows a TEST MODE banner.
              Disable only when moving to production with legal sign-off.
            </p>
          </div>
          <Switch checked={!!settings.test_mode} onCheckedChange={toggleTestMode} disabled={!isOwner} data-testid="settings-test-mode-switch" />
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-6">
        <h2 className="font-heading font-semibold text-base mb-2">Declaration wording · version {settings.declaration_version}</h2>
        <ol className="list-decimal pl-5 space-y-1.5 text-sm text-muted-foreground">
          {(settings.declaration_statements || []).map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      </CardContent></Card>

      <Card><CardContent className="p-6">
        <h2 className="font-heading font-semibold text-base mb-3">Site-wide disclaimers</h2>
        <div className="space-y-2 text-xs text-muted-foreground">
          {Object.entries(settings.disclaimers || {}).map(([k, v]) => (
            <p key={k}><Badge variant="outline" className="text-[10px] mr-2">{k}</Badge>{v}</p>
          ))}
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-6">
        <h2 className="font-heading font-semibold text-base mb-1">Email sending</h2>
        <p className="text-xs text-muted-foreground">
          EMAIL_SENDING_ENABLED=false — Phase 1 is draft-only. SMTP credentials are intentionally empty and no automated
          email is ever sent. Configure a dedicated domain and SMTP provider in a later phase.
        </p>
      </CardContent></Card>

      {isOwner && (
        <>
          <Card><CardContent className="p-6">
            <h2 className="font-heading font-semibold text-base mb-4 flex items-center gap-2"><UserPlus className="h-4 w-4" /> Administrators</h2>
            <div className="space-y-2 mb-5">
              {admins.map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3.5 py-2.5">
                  <div>
                    <p className="text-sm font-medium">{a.email}</p>
                    <p className="text-xs text-muted-foreground">role: {a.role} · status: {a.status}</p>
                  </div>
                  <Badge variant="outline" className={a.role === "owner" ? "bg-blue-50 text-blue-800 border-blue-200" : ""}>{a.role}</Badge>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-4 gap-3 items-end">
              <div>
                <Label className="text-xs">Email</Label>
                <Input className="mt-1" value={newAdmin.email} onChange={(e) => setNewAdmin((f) => ({ ...f, email: e.target.value }))} data-testid="new-admin-email" />
              </div>
              <div>
                <Label className="text-xs">Password (min 8)</Label>
                <Input type="password" className="mt-1" value={newAdmin.password} onChange={(e) => setNewAdmin((f) => ({ ...f, password: e.target.value }))} data-testid="new-admin-password" />
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={newAdmin.role} onValueChange={(v) => setNewAdmin((f) => ({ ...f, role: v }))}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="new-admin-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="reviewer">reviewer</SelectItem>
                    <SelectItem value="custodian">custodian</SelectItem>
                    <SelectItem value="owner">owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={createAdmin} disabled={!newAdmin.email || newAdmin.password.length < 8} data-testid="new-admin-create">Create admin</Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Roles: <strong>owner</strong> — everything incl. settings and elections; <strong>reviewer</strong> — applications and declaration review;{" "}
              <strong>custodian</strong> — evidence custody (workflow arrives in a later phase).
            </p>
          </CardContent></Card>

          <Card className="border-rose-200"><CardContent className="p-6">
            <h2 className="font-heading font-semibold text-base mb-1 flex items-center gap-2 text-rose-800"><ShieldAlert className="h-4 w-4" /> Danger zone</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Remove all declarations labelled TEST. Live declarations are never hard-deleted — this action only affects
              clearly-labelled test data and is recorded in the audit log.
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="purge-test-data-button">
                  <Trash2 className="h-4 w-4 mr-2" /> Purge TEST declarations
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Purge all TEST declarations?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently removes declarations flagged is_test=true. Live declarations are untouched. The action is audit-logged.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={purge} data-testid="purge-confirm-button">Purge</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}
